import { create } from 'zustand';
import { JanusSip, type SipState, type CallInfo, type NetworkHealth } from '@/lib/janus-sip';
import { Config, ActivityLogs, Auth, Agent, CrmTemplates, type CrmTemplate, type AgentProfile } from '@/lib/api';
import { startRingtone, stopRingtone } from '@/lib/ringtone';
import { isDesktop, showNativeNotification, setTrayStatus } from '@/lib/desktop';
import { openCrmPopup } from '@/lib/postmessage-bridge';
import { QualitySampler, type QualitySample } from '@/lib/call-quality';
import { headset } from '@/lib/headset';

interface SoftphoneState {
  client: JanusSip | null;
  state: SipState;
  call: CallInfo | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  onHold: boolean;
  queueIn: boolean;
  /** Feature-code dial in progress (login/logout/pause/resume). */
  queueAction: { kind: 'login' | 'logout' | 'pause' | 'resume'; status: 'pending' | 'done'; ts: number } | null;
  /** Agent paused queue receipt (mola). 3CX *63/*64 pause-queue feature code. */
  breakMode: boolean;
  autoAnswer: boolean;
  /** Son çağrı bitiş zamanı (ms epoch). "Boşta" (idle) sayacı bunu kullanır —
   *  çağrı biter bitmez sıfırdan başlasın diye (backend 30s poll lag'i olmadan). */
  lastCallEndedAt: number | null;
  /** Live call quality (MOS, loss, jitter, rtt, codec). Null when idle. */
  quality: QualitySample | null;
  /** WS + ICE birleşik ağ sağlığı. */
  health: NetworkHealth;
  log: string[];
  /** Removes window/document listeners installed at init(). Internal only. */
  networkListenerCleanup: (() => void) | null;
  /** True when navigator reports offline (Wi-Fi off, VPN down, ethernet
   *  unplugged). UI shows "İnternet yok" red, timer freezes, sipHealth poll
   *  pauses. Set by online/offline event listeners in init(). */
  networkOffline: boolean;
  /** Number of consecutive PBX unreachable polls (xapi 502/timeout). Updated
   *  by useSipHealthRecovery. Used by StatusPill to show "Bağlantı Yok" red
   *  when navigator.onLine lies (VPN dropped but Wi-Fi still up). */
  pbxUnreachableStreak: number;
  /** Popover anchor — açan butonun viewport koordinatları. Popover'lar
   *  buton altına sağ-hizalı yerleşir. null = viewport sağ-üstü fallback. */
  transferSheetOpen: boolean;
  transferSheetAnchor: PopoverAnchor | null;
  quickDialOpen: boolean;
  quickDialAnchor: PopoverAnchor | null;

  init: () => Promise<void>;
  destroy: () => void;
  dial: (number: string) => void;
  accept: () => void;
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  transfer: (number: string) => void;
  dtmf: (digit: string) => void;
  triggerQueueAction: (kind: 'login' | 'logout' | 'pause' | 'resume', code: string, reason?: BreakReason) => void;
  openTransferSheet: (anchor?: PopoverAnchor) => void;
  closeTransferSheet: () => void;
  openQuickDial: (anchor?: PopoverAnchor) => void;
  closeQuickDial: () => void;
  openBreakReasonPopover: (anchor?: PopoverAnchor) => void;
  closeBreakReasonPopover: () => void;

  breakReasonOpen: boolean;
  breakReasonAnchor: PopoverAnchor | null;
  /** Aktif olunduğunda son seçilen mola sebebi (resume olunca null). UI'da
   *  status pill'in altında göstermek için. */
  currentBreakReason: BreakReason | null;
}

/** Mola sebep türleri.
 *
 *  AGENT-SELF-SERVICE (3 sebep): mola, yemek, dis-arama
 *  LEADER-ASSIGNED   (4 sebep): egitim, toplanti, sinav, soru, geri-bildirim
 *
 *  Agent kendi popover'ında sadece self-service'i görür; takım lideri admin
 *  panelinden tüm sebeplerle başkasını molaya alabilir. Operasyonel ayrım —
 *  eğitim/toplantı/sınav gibi vardiya bozucular yöneticiye sorulmadan
 *  agent tarafından alınamaz.
 */
export type BreakReason =
  | 'mola'
  | 'yemek'
  | 'dis-arama'
  | 'egitim'
  | 'toplanti'
  | 'sinav'
  | 'soru'
  | 'geri-bildirim';

/** Bir buton'un viewport'taki bottom-right köşesi — popover'ın altına oturduğu yer. */
export interface PopoverAnchor {
  top: number;
  right: number;
}

// Per-call duration counters. Reset on every new call.
let holdStartedAt: number | null = null;
let muteStartedAt: number | null = null;
let totalHoldMs = 0;
let totalMuteMs = 0;

function resetCallCounters() {
  holdStartedAt = null;
  muteStartedAt = null;
  totalHoldMs = 0;
  totalMuteMs = 0;
}

// Per-call quality sampler — polls pc.getStats every 4s while in_call.
const qualitySampler = new QualitySampler();
let qualityIntervalId: ReturnType<typeof setInterval> | null = null;
function startQualityPolling(client: JanusSip) {
  qualitySampler.reset();
  stopQualityPolling();
  const tick = async () => {
    const pc: RTCPeerConnection | undefined = (client as any).sipHandle?.webrtcStuff?.pc;
    if (!pc) return;
    const sample = await qualitySampler.sample(pc);
    if (sample) {
      useSoftphone.setState({ quality: sample });
      // Her sample → console: bytesSent karşıya RTP gidiyor mu (=ses gönderiliyor mu)
      // bytesReceived karşıdan RTP geliyor mu (=ses duyuyoruz). Sıfırsa 3CX/PSTN sorunu.
      console.log(
        `[rtcstats] out=${sample.codecOut} in=${sample.codecIn} | ` +
        `bytesSent=${sample.bytesSent} bytesRecv=${sample.bytesReceived} | ` +
        `loss=${sample.lossPct.toFixed(1)}% jitter=${sample.jitterMs.toFixed(0)}ms rtt=${sample.rttMs.toFixed(0)}ms ` +
        `MOS=${sample.mos?.toFixed(2) ?? '—'}`,
      );
      if (sample.bytesSent === 0) {
        console.warn('[rtcstats] bytesSent=0 → mic RTP karşı tarafa GİTMİYOR (track add edilmemiş veya sender sessiz)');
      }
    }
  };
  // First sample after media flow stabilizes
  setTimeout(tick, 2000);
  qualityIntervalId = setInterval(tick, 4000);
}
function stopQualityPolling() {
  if (qualityIntervalId) {
    clearInterval(qualityIntervalId);
    qualityIntervalId = null;
  }
}

// Cached CRM templates (user + global merge from backend). Refreshed on init and
// re-fetched lazily when stale so newly-added admin templates kick in without re-login.
let crmTemplates: CrmTemplate[] = [];
let crmFetchedAt = 0;
const CRM_CACHE_TTL_MS = 30_000;
// Track which calls already triggered popup at each stage — prevents double-fire.
const crmFiredFor = new Set<string>();

async function refreshCrmTemplates() {
  try {
    crmTemplates = await CrmTemplates.list();
    crmFetchedAt = Date.now();
    console.log('[crm] templates refreshed', crmTemplates.length, crmTemplates);
  } catch (e) {
    console.warn('[crm] templates fetch failed', e);
    crmTemplates = [];
  }
}

function pickDefaultTemplate(stage: 'ring' | 'answered' | 'ended'): CrmTemplate | null {
  const eligible = crmTemplates.filter((t) => t.is_active && t.trigger_on === stage);
  if (eligible.length === 0) return null;
  return eligible.find((t) => t.is_default) ?? eligible[0];
}

async function maybeOpenCrm(
  stage: 'ring' | 'answered' | 'ended',
  call: CallInfo | null,
  extension?: string,
) {
  if (!call) return;
  // CRM popup only makes sense for inbound calls — agent looks up the caller.
  // Outbound calls (where agent dials someone) shouldn't pop the customer card.
  if (call.direction !== 'inbound') return;
  const key = `${call.callId ?? 'no-id'}:${stage}`;
  if (crmFiredFor.has(key)) return;

  // Lazy refresh if cache is stale — admin-added templates flow in without re-login.
  if (Date.now() - crmFetchedAt > CRM_CACHE_TTL_MS) {
    await refreshCrmTemplates();
  }

  const tpl = pickDefaultTemplate(stage);
  console.log('[crm] maybeOpenCrm', { stage, available: crmTemplates.length, picked: tpl?.name });
  if (!tpl) return;
  crmFiredFor.add(key);
  openCrmPopup(
    tpl.url_template,
    tpl.open_mode,
    {
      caller: call.direction === 'inbound' ? call.peer : extension,
      callee: call.direction === 'inbound' ? extension : call.peer,
      ext: extension,
      call_id: call.callId,
      direction: call.direction,
    },
    undefined,
    tpl.desktop_browser,
  );
}

export const useSoftphone = create<SoftphoneState>((set, get) => ({
  client: null,
  state: 'idle',
  call: null,
  remoteStream: null,
  muted: false,
  onHold: false,
  queueIn: false,
  queueAction: null,
  breakMode: false,
  autoAnswer: false,
  lastCallEndedAt: null,
  quality: null,
  health: 'ok',
  log: [],
  networkListenerCleanup: null,
  // navigator.onLine on mount — browser tracks this regardless of our hooks
  networkOffline: typeof navigator !== 'undefined' && !navigator.onLine,
  pbxUnreachableStreak: 0,
  transferSheetOpen: false,
  transferSheetAnchor: null,
  quickDialOpen: false,
  quickDialAnchor: null,
  breakReasonOpen: false,
  breakReasonAnchor: null,
  currentBreakReason: null,
  openTransferSheet: (anchor) => set({ transferSheetOpen: true, transferSheetAnchor: anchor ?? null, quickDialOpen: false, breakReasonOpen: false }),
  closeTransferSheet: () => set({ transferSheetOpen: false, transferSheetAnchor: null }),
  openQuickDial: (anchor) => set({ quickDialOpen: true, quickDialAnchor: anchor ?? null, transferSheetOpen: false, breakReasonOpen: false }),
  closeQuickDial: () => set({ quickDialOpen: false, quickDialAnchor: null }),
  openBreakReasonPopover: (anchor) => set({ breakReasonOpen: true, breakReasonAnchor: anchor ?? null, transferSheetOpen: false, quickDialOpen: false }),
  closeBreakReasonPopover: () => set({ breakReasonOpen: false, breakReasonAnchor: null }),

  init: async () => {
    if (get().client) return;
    const [pbx, creds, meRes] = await Promise.all([
      Config.pbx(),
      Config.sipCreds(),
      Auth.me().catch(() => ({ user: null })),
    ]);
    refreshCrmTemplates(); // fire-and-forget: popup is best-effort
    set({ autoAnswer: meRes.user?.auto_answer ?? false });
    const myExt = meRes.user?.extension;
    const client = new JanusSip({
      janusWsUrl: pbx.janus_ws_url,
      iceServers: pbx.ice_servers,
      iceTransportPolicy: pbx.ice_transport_policy,
      sipCredentials: creds,
      pbx: { host: pbx.pbx_host, port: pbx.pbx_port, fqdn: pbx.pbx_fqdn, transport: pbx.sip_transport },
    });
    await client.init();

    client.on('state', (s) => {
      const prev = get().state;
      console.log('[softphone-state] %s → %s', prev, s);
      set({ state: s });
      // Stop ring whenever we leave 'ringing' state (accept, reject, hangup)
      if (s !== 'ringing') stopRingtone();
      // CRM popup: fire on 'answered' stage when call connects (both directions)
      if (s === 'in_call') {
        maybeOpenCrm('answered', get().call, myExt);
        startQualityPolling(client);
      } else {
        stopQualityPolling();
        if (s !== 'ringing' && s !== 'calling') set({ quality: null });
      }
      // Update tray icon state (desktop only — no-op on web)
      if (isDesktop) {
        const trayState = s === 'in_call' ? 'in_call' : s === 'ringing' ? 'ringing' : s === 'registered' ? 'ready' : 'offline';
        setTrayStatus(trayState).catch(() => { /* */ });
      }
    });
    client.on('call', (c) => set({ call: c }));
    client.on('remoteStream', (s) => set({ remoteStream: s }));
    client.on('log', (line) => set((cur) => ({ log: [...cur.log.slice(-200), line] })));
    client.on('health', (h) => {
      set({ health: h });
      if (h !== 'ok') console.log('[health]', h);
    });
    // Janus/SIP hataları artık görünür — eskiden 'error' event'ini kimse dinlemiyordu,
    // bu yüzden accept (createAnswer/getUserMedia) hataları sessizce yutuluyordu.
    client.on('error', (e: any) => {
      console.error('[softphone] client error:', e?.message ?? e);
      set((cur) => ({ log: [...cur.log.slice(-200), `ERROR: ${e?.message ?? e}`] }));
    });

    client.on('incoming', (c) => {
      console.log('[softphone] incoming call', c);
      resetCallCounters();
      set({ muted: false, onHold: false }); // yeni çağrı temiz başlasın (stale mute/hold kalmasın)
      logActivity('incoming_call', c.callId, { peer: c.peer, display: c.displayName });

      // Native OS toast on desktop — pops even if window minimized
      if (isDesktop) {
        showNativeNotification('Gelen Çağrı', `${c.peer}${c.displayName ? ` — ${c.displayName}` : ''}`)
          .catch(() => { /* */ });
      }

      // CRM popup: fire on 'ring' stage if any template is configured for it
      maybeOpenCrm('ring', c, myExt);

      if (get().autoAnswer) {
        // Auto-answer: brief ring (300ms) for user awareness, then accept
        console.log('[softphone] auto-answer enabled — accepting in 300ms');
        startRingtone();
        setTimeout(() => {
          stopRingtone();
          get().accept();
        }, 300);
      } else {
        startRingtone();
      }
    });
    client.on('hangup', (reason) => {
      stopRingtone();
      const c = get().call;
      const durationMs = c?.answeredAt ? Date.now() - c.answeredAt : 0;
      const ringMs = c ? Date.now() - c.startedAt : 0;
      console.log(`[hangup] reason="${reason}" direction=${c?.direction} answered=${!!c?.answeredAt} duration=${durationMs}ms (ring→hangup=${ringMs}ms)`);
      if (c?.answeredAt && durationMs < 2000) {
        console.warn(`[hangup] ÇOK KISA çağrı (${durationMs}ms) — karşı taraf hemen kapattı veya RTP/ICE kurulamadı`);
      }
      // Flush any in-progress hold/mute into totals
      if (holdStartedAt !== null) totalHoldMs += Date.now() - holdStartedAt;
      if (muteStartedAt !== null) totalMuteMs += Date.now() - muteStartedAt;
      logActivity('call_ended', c?.callId, {
        reason,
        direction: c?.direction,
        peer: c?.peer,
        // Cevaplandı mı? Cevapsız tespitinin SAĞLAM sinyali — eski "gelen-cevaplanan"
        // çıkarma formülü asimetrik loglamada (answered>incoming) clamp ile 0 veriyordu.
        answered: !!c?.answeredAt,
        duration_ms: c?.answeredAt ? Date.now() - c.answeredAt : 0,
        hold_ms: totalHoldMs,
        mute_ms: totalMuteMs,
        hold_seconds: Math.round(totalHoldMs / 1000),
        mute_seconds: Math.round(totalMuteMs / 1000),
      });
      maybeOpenCrm('ended', c, myExt);
      // "Boşta" sayacı için: çağrı bitti → idle anchor şimdi (backend poll'unu bekleme).
      // Mute/hold state'ini de SIFIRLA + call'u temizle — yoksa karşı taraf kapatınca
      // mute butonu aktif/kırmızı takılı kalıyor + sonraki çağrı "muted" görünür başlıyor.
      set({ lastCallEndedAt: Date.now(), muted: false, onHold: false, call: null });
      // Drop fired-flags for this call so memory doesn't grow forever
      if (c?.callId) {
        crmFiredFor.delete(`${c.callId}:ring`);
        crmFiredFor.delete(`${c.callId}:answered`);
        crmFiredFor.delete(`${c.callId}:ended`);
      }
      resetCallCounters();
    });
    // janus_registered'i sadece İLK registration'da log et — reconnect/watchdog
    // cycle'larında her seferinde log'lamak Activity feed'i sip event'leriyle
    // doldurur (dakikalar içinde 50+ entry birikir, gerçek call activity'sini
    // gölgeler). Sonraki re-register'lar console'da görünmeye devam eder.
    let didLogInitialRegister = false;
    client.on('registered', () => {
      if (didLogInitialRegister) return;
      didLogInitialRegister = true;
      logActivity('janus_registered');
    });

    await client.connect();
    client.register();
    set({ client });

    // Headset HID — pass handlers so its buttons can toggle mute/hold via
    // the store (which knows current state), then bind client for state-sync.
    headset.setHandlers({
      toggleMute: () => get().toggleMute(),
      toggleHold: () => get().toggleHold(),
    });
    void headset.bindClient(client);

    // ─── REGISTER liveness — react to network and visibility changes ────
    // Without these, a transient network blip or PBX restart leaves the UI
    // showing "Hazır" while the agent is actually unregistered on 3CX. The
    // user has to refresh the browser to recover — exactly the symptom we
    // saw in production. Sofia-SIP's short register_ttl (120s) auto-renews,
    // but proactive recovery on observable network events is faster (<1s).
    const onOnline = () => {
      console.log('[network] online detected — resuming reconnect');
      set({ networkOffline: false });
      client.resumeReconnect();
    };
    const onOffline = () => {
      // Browser knows the network is down (TCP/DNS failing). Pause the WS
      // reconnect cycle — otherwise we spam attempts every 1-30s, defeat
      // the backoff (WS open → instant close cycle), and flood the console.
      // resumeReconnect() on `online` event will recover.
      console.log('[network] offline detected — pausing reconnect');
      set({ networkOffline: true });
      client.pauseReconnect();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[visibility] tab visible — probing registration');
        // If we're in 'registered' state, Sofia-SIP's OPTIONS keepalive will
        // tell us within seconds if we're stale. If we're already in a bad
        // state, force re-register immediately.
        if (get().state !== 'registered' && get().state !== 'in_call' && get().state !== 'ringing' && get().state !== 'calling') {
          client.forceRegister();
        }
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibility);
    // Store removers so destroy() can unbind — without this the listeners
    // pile up across logins and force-register a destroyed client.
    set({ networkListenerCleanup: () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibility);
    } });
  },

  destroy: () => {
    stopRingtone();
    headset.destroy();
    get().networkListenerCleanup?.();
    get().client?.destroy();
    set({ client: null, state: 'idle', call: null, remoteStream: null, networkListenerCleanup: null });
  },

  dial: (number) => {
    resetCallCounters();
    set({ muted: false, onHold: false }); // yeni çağrı temiz başlasın
    get().client?.call(number);
    logActivity('call_started', undefined, { direction: 'outbound', target: number });
  },

  accept: () => {
    const c = get().call;
    console.log('[softphone] accept() clicked — state=%s call=%o client=%s', get().state, c?.callId, get().client ? 'set' : 'NULL');
    stopRingtone();
    get().client?.accept();
    logActivity('call_answered', c?.callId, { direction: 'inbound', peer: c?.peer });
  },

  reject: () => {
    stopRingtone();
    get().client?.reject();
    const c = get().call;
    logActivity('call_rejected', c?.callId, { peer: c?.peer });
  },

  hangup: () => {
    stopRingtone();
    get().client?.hangup();
  },

  toggleMute: () => {
    const next = !get().muted;
    get().client?.setMute(next);
    set({ muted: next });
    if (next) {
      muteStartedAt = Date.now();
    } else if (muteStartedAt !== null) {
      totalMuteMs += Date.now() - muteStartedAt;
      muteStartedAt = null;
    }
    logActivity(next ? 'call_muted' : 'call_unmuted', get().call?.callId, {
      total_mute_ms_so_far: totalMuteMs + (muteStartedAt ? Date.now() - muteStartedAt : 0),
    });
  },

  toggleHold: () => {
    const next = !get().onHold;
    if (next) get().client?.hold();
    else get().client?.unhold();
    set({ onHold: next });
    if (next) {
      holdStartedAt = Date.now();
    } else if (holdStartedAt !== null) {
      totalHoldMs += Date.now() - holdStartedAt;
      holdStartedAt = null;
    }
    logActivity(next ? 'call_held' : 'call_unheld', get().call?.callId, {
      total_hold_ms_so_far: totalHoldMs + (holdStartedAt ? Date.now() - holdStartedAt : 0),
    });
  },

  transfer: (number) => {
    get().client?.transferBlind(number);
    logActivity('call_transferred', get().call?.callId, { to: number, kind: 'blind' });
  },

  dtmf: (digit) => {
    get().client?.dtmf(digit);
    logActivity('dtmf_sent', get().call?.callId, { digit });
  },

  triggerQueueAction: (kind, _code, reason) => {
    const cur = get();
    if (cur.state !== 'registered') {
      console.warn('[queue] cannot trigger — SIP state is', cur.state);
      return;
    }
    if (cur.queueAction) {
      console.warn('[queue] previous queueAction still pending — resetting and continuing');
    }
    // TAMAMEN xapi tabanlı (eski IGDAS C# projesi mapping'i, 4 yıl prod kanıtlı):
    //   login   → PATCH QueueStatus=LoggedIn  + CurrentProfileName=Available
    //   logout  → PATCH QueueStatus=LoggedOut (profile dokunulmaz)
    //   pause   → PATCH QueueStatus=LoggedOut + CurrentProfileName(mola) + ProfileMessage
    //   resume  → PATCH CurrentProfileName=Available SADECE (QueueStatus'a dokunma →
    //             kuyruk-dışı kalır, agent kuyruğa kendisi girer)
    //
    // SIP feature code dial (*61-*64) artık yok — 3CX V20 stock'ta bu kodlar
    // configure değil ve "Lunch" gibi profile name'ler xapi'da 400 dönüyordu.
    // Eski TCX.Configuration SDK aynı PATCH'i yapıyordu, biz xapi REST ile.

    // Reason → (profile, customMessage) — eski projenin dashboard.js:464-473
    // tablosundan birebir kopya:
    //   Mola=Away, Yemek="Out of office", D.Arama="Custom 1",
    //   Custom 2 + ProfileMessage = Eğitim/Toplantı/Sınav/Soru Molası/Geri Bildirim
    const breakMap: Record<NonNullable<typeof reason>, { profile: AgentProfile; msg: string }> = {
      'mola':          { profile: 'Away',          msg: '' },
      'yemek':         { profile: 'Out of office', msg: '' },
      'dis-arama':     { profile: 'Custom 1',      msg: '' },
      'egitim':        { profile: 'Custom 2',      msg: 'Eğitim' },
      'toplanti':      { profile: 'Custom 2',      msg: 'Toplantı' },
      'sinav':         { profile: 'Custom 2',      msg: 'Sınav' },
      'soru':          { profile: 'Custom 2',      msg: 'Soru Molası' },
      'geri-bildirim': { profile: 'Custom 2',      msg: 'Geri Bildirim' },
    };

    const ts = Date.now();
    const patch: any = { queueAction: { kind, status: 'pending', ts } };
    if (kind === 'pause') patch.currentBreakReason = reason ?? 'mola';
    if (kind === 'resume') patch.currentBreakReason = null;
    set(patch);

    const activityType = kind === 'login' ? 'queue_login'
      : kind === 'logout' ? 'queue_logout'
      : kind === 'pause' ? 'queue_paused'
      : 'queue_resumed';
    const meta: Record<string, unknown> = {};
    if (kind === 'pause') {
      const r = reason ?? 'mola';
      const m = breakMap[r];
      meta.reason = r;
      meta.profile = m.profile;
      if (m.msg) meta.customMessage = m.msg;
    }
    logActivity(activityType, undefined, meta);

    const finishOk = () => {
      const doneTs = Date.now();
      const donePatch: any = { queueAction: { kind, status: 'done', ts: doneTs } };
      if (kind === 'login')  donePatch.queueIn = true;
      if (kind === 'logout') { donePatch.queueIn = false; donePatch.breakMode = false; donePatch.currentBreakReason = null; }
      // Mola = kuyruktan çık (çağrı almasın). Moladan dönünce kuyruğa OTOMATİK
      // GİRME — agent kuyruk-dışı döner, kuyruğa kendisi girer ("Döndüğünde
      // kuyruğa girmelisin" mesajıyla tutarlı).
      if (kind === 'pause')  { donePatch.breakMode = true;  donePatch.queueIn = false; }
      if (kind === 'resume') { donePatch.breakMode = false; donePatch.queueIn = false; }
      set(donePatch);
      setTimeout(() => {
        const cur2 = get().queueAction;
        if (cur2 && cur2.status === 'done' && cur2.ts === doneTs) {
          set({ queueAction: null });
        }
      }, 1600);
    };

    const finishErr = (label: string, err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const msg = err?.message;
      console.error(`[queue] ${label} FAILED`, { kind, reason, status, data, msg });
      if (data?.message) console.error('[queue] backend message:', data.message);
      const rollback: any = { queueAction: null };
      if (kind === 'pause') rollback.currentBreakReason = null;
      set(rollback);
    };

    if (kind === 'login') {
      // QueueStatus=LoggedIn, sonra Available'a çek (önce mola/yemek modundaysa
      // resume olmadan kuyruğa dönüş gerek). İki PATCH ardı ardına.
      console.log('[queue] xapi login (QueueStatus=LoggedIn + profile=Available)');
      Agent.setQueue(true)
        .then(() => Agent.setProfile('Available'))
        .then(finishOk)
        .catch((e) => finishErr('Agent.setQueue/Profile(login)', e));
    } else if (kind === 'logout') {
      // Sadece QueueStatus toggle — profile dokunulmaz. Eski projenin
      // dashboard.js:442-444 mantığı.
      console.log('[queue] xapi logout (QueueStatus=LoggedOut)');
      Agent.setQueue(false)
        .then(finishOk)
        .catch((e) => finishErr('Agent.setQueue(logout)', e));
    } else if (kind === 'pause') {
      const r = reason ?? 'mola';
      const m = breakMap[r];
      // Molaya çıkınca KUYRUKTAN ÇIK (çağrı almasın) + mola profilini ayarla.
      console.log('[queue] xapi pause → setQueue(false) + profile=', m.profile, 'msg=', m.msg || '-');
      Agent.setQueue(false)
        .then(() => Agent.setProfile(m.profile, m.msg || undefined))
        .then(finishOk)
        .catch((e) => finishErr('Agent.setQueue/Profile(pause)', e));
    } else if (kind === 'resume') {
      // Moladan dönünce SADECE profili Available yap — kuyruğa OTOMATİK GİRME.
      // setQueue(true) YOK; agent kuyruk-dışı + Available döner, kuyruğa kendisi
      // girer. (QueueStatus zaten pause'da LoggedOut yapılmıştı.)
      console.log('[queue] xapi resume → profile=Available (kuyruğa otomatik girmez)');
      Agent.setProfile('Available')
        .then(finishOk)
        .catch((e) => finishErr('Agent.setProfile(resume)', e));
    }
  },
}));

function logActivity(type: string, refId?: string, meta?: Record<string, unknown>) {
  ActivityLogs.log(type, refId, meta).catch(() => { /* swallow — best-effort */ });
}
